import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { appRoutes } from "@/routesConfig";
import SidebarHeader from "./SidebarHeader";

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function Sidebar({
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const { user } = useAuth();
  const { t } = useTranslation("common");

  const filteredNavigation = appRoutes.filter((route) => {
    // Only routes with a label are displayed in sidebar
    if (!route.label) return false;
    if (user) return true;
    return route.public;
  });

  return (
    <div className="hidden lg:flex lg:flex-shrink-0">
      <div
        className={clsx(
          "bg-white border-r border-secondary-200 min-h-screen transition-all duration-300 ease-in-out",
          collapsed ? "w-16" : "w-64",
        )}
      >
        {filteredNavigation.length > 0 && (
          <SidebarHeader
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            canCreate={!!user}
          />
        )}
        <nav className="mt-4">
          <div className="px-3">
            <ul className="space-y-1">
              {filteredNavigation.map((route, index) => (
                <li key={route.label}>
                  {route.group &&
                    route.group !== filteredNavigation[index - 1]?.group && (
                      <div className="mt-4 mb-2 border-t border-secondary-200 pt-3">
                        {!collapsed && (
                          <span className="px-2 text-[10px] font-semibold tracking-[0.14em] uppercase text-secondary-400">
                            {t(route.group)}
                          </span>
                        )}
                      </div>
                    )}
                  <NavLink
                    to={route.path}
                    className={({ isActive }) =>
                      clsx(
                        "group flex items-center px-2 py-2 text-sm font-medium rounded-lg",
                        isActive
                          ? "bg-primary-50 text-primary-700"
                          : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900",
                      )
                    }
                    title={
                      collapsed && route.label ? t(route.label) : undefined
                    }
                  >
                    {route.icon && (
                      <route.icon
                        className={clsx(
                          "flex-shrink-0",
                          collapsed ? "h-5 w-5 mx-auto" : "mr-3 h-5 w-5",
                        )}
                        aria-hidden="true"
                      />
                    )}
                    {!collapsed && route.label && t(route.label)}
                    {!collapsed && route.badge && (
                      <span className="ml-auto rounded-full bg-primary-500 px-1.5 py-0.5 text-[9px] font-bold tracking-[0.08em] text-white">
                        {t(route.badge)}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
}
